import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
  Grid,
  Link,
  Input,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useForm, Controller } from 'react-hook-form';
import { expensesApi, Expense, CreateExpenseData, UpdateExpenseData, ExpenseFilters } from '../services/expenses';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import { CURRENCIES } from '../utils/currency';
import { useCompany } from '../contexts/CompanyContext';

// Predefined expense categories
const PREDEFINED_CATEGORIES = [
  'Travel',
  'Office Supplies',
  'Software',
  'Hardware',
  'Marketing',
  'Professional Services',
  'Utilities',
  'Rent',
  'Meals & Entertainment',
  'Training & Education',
  'Insurance',
  'Other',
];

const Expenses: React.FC = () => {
  const { baseCurrency } = useCompany();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [viewingExpense, setViewingExpense] = useState<Expense | null>(null);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  const [categories, setCategories] = useState<string[]>(PREDEFINED_CATEGORIES);
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  
  // Tabs
  const [activeTab, setActiveTab] = useState(0);
  
  // Filters
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const expenseForm = useForm<CreateExpenseData>({
    defaultValues: {
      description: '',
      amount: 0,
      expenseDate: format(new Date(), 'yyyy-MM-dd'),
      category: '',
      currency: baseCurrency,
      vendor: '',
      notes: '',
      expenseType: 'regular',
      standingOrderFrequency: undefined,
      standingOrderPaymentDay: undefined,
      usefulLifeYears: undefined,
      depreciationMethod: undefined,
      salvageValue: undefined,
    },
  });

  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);

  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: ExpenseFilters = {};
      if (startDateFilter) filters.startDate = startDateFilter;
      if (endDateFilter) filters.endDate = endDateFilter;
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      if (statusFilter !== 'all') filters.status = statusFilter as 'draft' | 'submitted' | 'approved' | 'rejected';
      
      // Filter by expense type based on active tab
      if (activeTab === 1) {
        filters.expenseType = 'standing_order';
      } else if (activeTab === 2) {
        filters.expenseType = 'capital_expenditure';
      }
      // activeTab === 0 means "All Expenses", so no filter
      
      const expensesData = await expensesApi.getAll(filters);
      let filtered = expensesData || [];
      
      // Filter by vendor (client-side since backend doesn't support it)
      if (vendorFilter) {
        filtered = filtered.filter(exp => 
          exp.vendor?.toLowerCase().includes(vendorFilter.toLowerCase())
        );
      }
      
      setExpenses(filtered);
    } catch (err: any) {
      console.error('Error loading expenses:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load expenses';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [startDateFilter, endDateFilter, categoryFilter, statusFilter, vendorFilter, activeTab]);

  const loadCategories = useCallback(async () => {
    try {
      const expensesData = await expensesApi.getAll();
      const allCategories = new Set<string>(PREDEFINED_CATEGORIES);
      expensesData.forEach(exp => {
        if (exp.category && !PREDEFINED_CATEGORIES.includes(exp.category)) {
          allCategories.add(exp.category);
        }
      });
      setCategories(Array.from(allCategories).sort());
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleOpenCreateDialog = () => {
    // Set default expense type based on active tab
    let defaultExpenseType: 'regular' | 'standing_order' | 'capital_expenditure' = 'regular';
    if (activeTab === 1) {
      defaultExpenseType = 'standing_order';
    } else if (activeTab === 2) {
      defaultExpenseType = 'capital_expenditure';
    }
    
    expenseForm.reset({
      description: '',
      amount: 0,
      expenseDate: format(new Date(), 'yyyy-MM-dd'),
      category: '',
      currency: baseCurrency,
      vendor: '',
      notes: '',
      expenseType: defaultExpenseType,
      standingOrderFrequency: undefined,
      standingOrderPaymentDay: undefined,
      usefulLifeYears: undefined,
      depreciationMethod: undefined,
      salvageValue: undefined,
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    setShowCustomCategory(false);
    setCustomCategory('');
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    expenseForm.reset();
    setReceiptFile(null);
    setReceiptPreview(null);
    setShowCustomCategory(false);
    setCustomCategory('');
  };

  const handleOpenEditDialog = (expense: Expense) => {
    setEditingExpense(expense);
    expenseForm.reset({
      description: expense.description,
      amount: expense.amount,
      expenseDate: format(parseISO(expense.expenseDate), 'yyyy-MM-dd'),
      category: expense.category,
      currency: expense.currency,
      vendor: expense.vendor || '',
      notes: expense.notes || '',
      expenseType: expense.expenseType || 'regular',
      standingOrderFrequency: expense.standingOrderFrequency || undefined,
      standingOrderPaymentDay: expense.standingOrderPaymentDay || undefined,
      usefulLifeYears: expense.usefulLifeYears || undefined,
      depreciationMethod: expense.depreciationMethod || undefined,
      salvageValue: expense.salvageValue || undefined,
    });
    setReceiptFile(null);
    setReceiptPreview(expense.receiptPath ? expensesApi.getReceiptUrl(expense.receiptPath) : null);
    setShowCustomCategory(false);
    setCustomCategory('');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingExpense(null);
    expenseForm.reset();
    setReceiptFile(null);
    setReceiptPreview(null);
    setShowCustomCategory(false);
    setCustomCategory('');
  };

  const handleOpenViewDialog = (expense: Expense) => {
    setViewingExpense(expense);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingExpense(null);
  };

  const handleOpenDeleteDialog = (expense: Expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setExpenseToDelete(null);
  };

  const handleReceiptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result as string);
      };
      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleCreateExpense = async (data: CreateExpenseData) => {
    try {
      setError(null);
      const category = showCustomCategory && customCategory ? customCategory : data.category;
      await expensesApi.create({
        ...data,
        category,
        receipt: receiptFile || undefined,
      });
      handleCloseCreateDialog();
      loadExpenses();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create expense');
    }
  };

  const handleUpdateExpense = async (data: UpdateExpenseData) => {
    if (!editingExpense) return;

    try {
      setError(null);
      const category = showCustomCategory && customCategory ? customCategory : data.category;
      await expensesApi.update(editingExpense.id, {
        ...data,
        category,
        receipt: receiptFile || undefined,
      });
      handleCloseEditDialog();
      loadExpenses();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update expense');
    }
  };

  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;

    try {
      setError(null);
      await expensesApi.delete(expenseToDelete.id);
      handleCloseDeleteDialog();
      loadExpenses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete expense');
    }
  };

  const handleSubmitForApproval = async (expense: Expense) => {
    try {
      setError(null);
      await expensesApi.submit(expense.id);
      loadExpenses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit expense for approval');
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'submitted': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getTotalExpenses = () => {
    return expenses.reduce((sum, exp) => sum + exp.amount, 0);
  };

  const getUniqueVendors = () => {
    const vendors = new Set<string>();
    expenses.forEach(exp => {
      if (exp.vendor) vendors.add(exp.vendor);
    });
    return Array.from(vendors).sort();
  };

  if (loading && expenses.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Expenses</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Add Expense
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="All Expenses" />
          <Tab label="Standing Orders" />
          <Tab label="Capital Expenditure" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={vendorFilter}
                label="Vendor"
                onChange={(e) => setVendorFilter(e.target.value)}
              >
                <MenuItem value="">All Vendors</MenuItem>
                {getUniqueVendors().map((vendor) => (
                  <MenuItem key={vendor} value={vendor}>
                    {vendor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setStartDateFilter('');
                setEndDateFilter('');
                setCategoryFilter('all');
                setVendorFilter('');
                setStatusFilter('all');
              }}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
        {expenses.length > 0 && (
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Total: {formatCurrency(getTotalExpenses(), expenses[0]?.currency || 'USD')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Expenses Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary">No expenses found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      {format(parseISO(expense.expenseDate), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>{expense.description}</TableCell>
                    <TableCell>
                      <Chip 
                        label={
                          expense.expenseType === 'standing_order' ? 'Standing Order' :
                          expense.expenseType === 'capital_expenditure' ? 'Capital Expenditure' :
                          'Regular'
                        } 
                        size="small" 
                        color={expense.expenseType === 'capital_expenditure' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={expense.category} size="small" />
                    </TableCell>
                    <TableCell>{expense.vendor || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={expense.status ? expense.status.charAt(0).toUpperCase() + expense.status.slice(1) : 'Draft'} 
                        size="small" 
                        color={getStatusColor(expense.status) as any}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(expense.amount, expense.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {expense.receiptPath ? (
                        <Link
                          href={expensesApi.getReceiptUrl(expense.receiptPath)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconButton size="small">
                            <AttachFileIcon />
                          </IconButton>
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenViewDialog(expense)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {expense.status === 'draft' && (
                          <Tooltip title="Submit for Approval">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSubmitForApproval(expense)}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {expense.status !== 'approved' && expense.status !== 'rejected' && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(expense)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {expense.status === 'draft' && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(expense)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Expense Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <form onSubmit={expenseForm.handleSubmit(handleCreateExpense)}>
          <DialogTitle>Add Expense</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Description"
                required
                {...expenseForm.register('description', { required: 'Description is required' })}
                error={!!expenseForm.formState.errors.description}
                helperText={expenseForm.formState.errors.description?.message}
              />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    inputProps={{ step: 0.01, min: 0 }}
                    {...expenseForm.register('amount', {
                      required: 'Amount is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Amount must be positive' },
                    })}
                    error={!!expenseForm.formState.errors.amount}
                    helperText={expenseForm.formState.errors.amount?.message}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="currency"
                    control={expenseForm.control}
                    rules={{ required: 'Currency is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Expense Date"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                {...expenseForm.register('expenseDate', { required: 'Date is required' })}
                error={!!expenseForm.formState.errors.expenseDate}
                helperText={expenseForm.formState.errors.expenseDate?.message}
              />
              <Controller
                name="expenseType"
                control={expenseForm.control}
                rules={{ required: 'Expense type is required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth required error={!!fieldState.error}>
                    <InputLabel>Expense Type</InputLabel>
                    <Select {...field} label="Expense Type">
                      <MenuItem value="regular">Regular Expense</MenuItem>
                      <MenuItem value="standing_order">Standing Order</MenuItem>
                      <MenuItem value="capital_expenditure">Capital Expenditure</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <Box>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={showCustomCategory ? 'custom' : expenseForm.watch('category')}
                    label="Category"
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setShowCustomCategory(true);
                        expenseForm.setValue('category', '');
                      } else {
                        setShowCustomCategory(false);
                        expenseForm.setValue('category', e.target.value);
                      }
                    }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">+ Add Custom Category</MenuItem>
                  </Select>
                </FormControl>
                {showCustomCategory && (
                  <TextField
                    fullWidth
                    label="Custom Category"
                    sx={{ mt: 2 }}
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    required
                  />
                )}
              </Box>
              <TextField
                fullWidth
                label="Vendor (Optional)"
                {...expenseForm.register('vendor')}
              />
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                {...expenseForm.register('notes')}
              />
              <Box>
                <InputLabel sx={{ mb: 1 }}>Receipt (Optional)</InputLabel>
                <Input
                  type="file"
                  inputProps={{ accept: 'image/*,.pdf' }}
                  onChange={handleReceiptChange}
                />
                {receiptPreview && (
                  <Box mt={2}>
                    {receiptFile?.type.startsWith('image/') ? (
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        PDF file selected: {receiptFile?.name}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={showCustomCategory && !customCategory}
            >
              Create Expense
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <form onSubmit={expenseForm.handleSubmit(handleUpdateExpense)}>
          <DialogTitle>Edit Expense</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Description"
                required
                {...expenseForm.register('description', { required: 'Description is required' })}
                error={!!expenseForm.formState.errors.description}
                helperText={expenseForm.formState.errors.description?.message}
              />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    required
                    inputProps={{ step: 0.01, min: 0 }}
                    {...expenseForm.register('amount', {
                      required: 'Amount is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Amount must be positive' },
                    })}
                    error={!!expenseForm.formState.errors.amount}
                    helperText={expenseForm.formState.errors.amount?.message}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="currency"
                    control={expenseForm.control}
                    rules={{ required: 'Currency is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Expense Date"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                {...expenseForm.register('expenseDate', { required: 'Date is required' })}
                error={!!expenseForm.formState.errors.expenseDate}
                helperText={expenseForm.formState.errors.expenseDate?.message}
              />
              <Controller
                name="expenseType"
                control={expenseForm.control}
                rules={{ required: 'Expense type is required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth required error={!!fieldState.error}>
                    <InputLabel>Expense Type</InputLabel>
                    <Select 
                      {...field} 
                      label="Expense Type"
                      onChange={(e) => {
                        field.onChange(e);
                        if (e.target.value === 'standing_order') {
                          // Clear CapEx fields
                          expenseForm.setValue('usefulLifeYears', undefined);
                          expenseForm.setValue('depreciationMethod', undefined);
                          expenseForm.setValue('salvageValue', undefined);
                        } else if (e.target.value === 'capital_expenditure') {
                          // Clear standing order fields
                          expenseForm.setValue('standingOrderFrequency', undefined);
                          expenseForm.setValue('standingOrderPaymentDay', undefined);
                        } else {
                          // Clear both
                          expenseForm.setValue('standingOrderFrequency', undefined);
                          expenseForm.setValue('standingOrderPaymentDay', undefined);
                          expenseForm.setValue('usefulLifeYears', undefined);
                          expenseForm.setValue('depreciationMethod', undefined);
                          expenseForm.setValue('salvageValue', undefined);
                        }
                      }}
                    >
                      <MenuItem value="regular">Regular Expense</MenuItem>
                      <MenuItem value="standing_order">Standing Order</MenuItem>
                      <MenuItem value="capital_expenditure">Capital Expenditure</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              {/* Standing Order Frequency and Payment Day */}
              {expenseForm.watch('expenseType') === 'standing_order' && (
                <>
                  <Controller
                    name="standingOrderFrequency"
                    control={expenseForm.control}
                    rules={{ required: 'Frequency is required for standing orders' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <InputLabel>Frequency</InputLabel>
                        <Select 
                          {...field} 
                          label="Frequency"
                          onChange={(e) => {
                            field.onChange(e);
                            // Clear payment day if not monthly
                            if (e.target.value !== 'monthly') {
                              expenseForm.setValue('standingOrderPaymentDay', undefined);
                            }
                          }}
                        >
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="quarterly">Quarterly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                  {expenseForm.watch('standingOrderFrequency') === 'monthly' && (
                    <Controller
                      name="standingOrderPaymentDay"
                      control={expenseForm.control}
                      rules={{ 
                        required: 'Payment day is required for monthly standing orders',
                        min: { value: 1, message: 'Day must be between 1 and 31' },
                        max: { value: 31, message: 'Day must be between 1 and 31' }
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Payment Day (1-31)"
                          required
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{ min: 1, max: 31 }}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value) && value >= 1 && value <= 31) {
                              field.onChange(value);
                            } else if (e.target.value === '') {
                              field.onChange(undefined);
                            }
                          }}
                        />
                      )}
                    />
                  )}
                </>
              )}
              {/* Capital Expenditure Fields */}
              {expenseForm.watch('expenseType') === 'capital_expenditure' && (
                <>
                  <Controller
                    name="usefulLifeYears"
                    control={expenseForm.control}
                    rules={{ 
                      required: 'Useful life is required for capital expenditure',
                      min: { value: 1, message: 'Useful life must be at least 1 year' },
                      max: { value: 100, message: 'Useful life cannot exceed 100 years' }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        label="Useful Life (Years)"
                        required
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || 'Expected useful life of the asset in years'}
                        inputProps={{ min: 1, max: 100 }}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 1) {
                            field.onChange(value);
                          } else if (e.target.value === '') {
                            field.onChange(undefined);
                          }
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="depreciationMethod"
                    control={expenseForm.control}
                    rules={{ required: 'Depreciation method is required for capital expenditure' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <InputLabel>Depreciation Method</InputLabel>
                        <Select {...field} label="Depreciation Method">
                          <MenuItem value="straight_line">Straight Line</MenuItem>
                          <MenuItem value="declining_balance">Declining Balance</MenuItem>
                          <MenuItem value="units_of_production">Units of Production</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="salvageValue"
                    control={expenseForm.control}
                    rules={{ 
                      min: { value: 0, message: 'Salvage value cannot be negative' }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        label="Salvage Value (Optional)"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || 'Expected residual value at end of useful life'}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value) && value >= 0) {
                            field.onChange(value);
                          } else if (e.target.value === '') {
                            field.onChange(undefined);
                          }
                        }}
                      />
                    )}
                  />
                </>
              )}
              <Box>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={showCustomCategory ? 'custom' : expenseForm.watch('category')}
                    label="Category"
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setShowCustomCategory(true);
                        expenseForm.setValue('category', '');
                      } else {
                        setShowCustomCategory(false);
                        expenseForm.setValue('category', e.target.value);
                      }
                    }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">+ Add Custom Category</MenuItem>
                  </Select>
                </FormControl>
                {showCustomCategory && (
                  <TextField
                    fullWidth
                    label="Custom Category"
                    sx={{ mt: 2 }}
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    required
                  />
                )}
              </Box>
              <TextField
                fullWidth
                label="Vendor (Optional)"
                {...expenseForm.register('vendor')}
              />
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                {...expenseForm.register('notes')}
              />
              <Box>
                <InputLabel sx={{ mb: 1 }}>Receipt (Optional - upload new to replace)</InputLabel>
                {editingExpense?.receiptPath && !receiptFile && (
                  <Box mb={1}>
                    <Link
                      href={expensesApi.getReceiptUrl(editingExpense.receiptPath)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View current receipt
                    </Link>
                  </Box>
                )}
                <Input
                  type="file"
                  inputProps={{ accept: 'image/*,.pdf' }}
                  onChange={handleReceiptChange}
                />
                {receiptPreview && (
                  <Box mt={2}>
                    {receiptFile?.type.startsWith('image/') ? (
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        PDF file selected: {receiptFile?.name}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={showCustomCategory && !customCategory}
            >
              Update Expense
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Expense Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Expense Details</DialogTitle>
        <DialogContent>
          {viewingExpense && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{viewingExpense.description}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(viewingExpense.amount, viewingExpense.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                  <Typography variant="body1">
                    {format(parseISO(viewingExpense.expenseDate), 'MMM d, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                  <Chip label={viewingExpense.category} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Expense Type</Typography>
                  <Chip 
                    label={
                      viewingExpense.expenseType === 'standing_order' ? 'Standing Order' :
                      viewingExpense.expenseType === 'capital_expenditure' ? 'Capital Expenditure' :
                      'Regular'
                    } 
                    size="small" 
                    color={viewingExpense.expenseType === 'capital_expenditure' ? 'primary' : 'default'}
                  />
                </Grid>
                {viewingExpense.vendor && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Vendor</Typography>
                    <Typography variant="body1">{viewingExpense.vendor}</Typography>
                  </Grid>
                )}
                {viewingExpense.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{viewingExpense.notes}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={viewingExpense.status ? viewingExpense.status.charAt(0).toUpperCase() + viewingExpense.status.slice(1) : 'Draft'} 
                    size="small" 
                    color={getStatusColor(viewingExpense.status) as any}
                  />
                </Grid>
                {viewingExpense.rejectionReason && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="subtitle2">Rejection Reason:</Typography>
                      <Typography variant="body2">{viewingExpense.rejectionReason}</Typography>
                    </Alert>
                  </Grid>
                )}
                {viewingExpense.receiptPath && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Receipt</Typography>
                    <Link
                      href={expensesApi.getReceiptUrl(viewingExpense.receiptPath)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Receipt
                    </Link>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Created: {format(parseISO(viewingExpense.createdAt), 'MMM d, yyyy HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Expense Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this expense?
            <br />
            <br />
            Description: {expenseToDelete?.description}
            <br />
            Amount: {expenseToDelete ? formatCurrency(expenseToDelete.amount, expenseToDelete.currency) : ''}
            <br />
            Date: {expenseToDelete ? format(parseISO(expenseToDelete.expenseDate), 'MMM d, yyyy') : ''}
            <br />
            <br />
            This action cannot be undone.
      </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteExpense} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
